import React from 'react';
import { useIntl } from 'react-intl';

import { useConfig } from '@xingternal/hops';

import { Head } from '@/components/_common';
import { useSearch } from '@/domain/search/hooks';

import { ANY, CITY, EMPLOYMENT_TYPE, ROLE, TRANSLATIONS_MAP } from '../../constants';
import { SerpsRouteInfo } from '../../hooks/use-serps-match';
import { getCategoryTranslation } from '../../utils/get-category-translation';
import {
  capitalizeFirstLetter,
  getSeoSerpsMetaDescriptionValues,
  getSeoSerpsTitleTag,
} from '../../utils/get-seo-serps-data';

type Props = {
  serps: SerpsRouteInfo;
};

const getTitleOverride = (values: Record<string, string>): string => {
  return `${values.employmentType === 'Studierende' ? 'Studentenjobs' : 'Freelancer Jobs'} in ${values.city} - ${
    values.month
  } ${values.year}`;
};

const getDescriptionOverride = (values: Record<string, string>): string =>
  `${values.postings} ${values.employmentType === 'Studierende' ? 'Studentenjobs' : 'Freelancer Jobs'} in ${
    values.city
  } im XING Jobs-Netzwerk. Finde den Job, der zu Dir passt.`;

export const SerpsMetadata: React.VFC<Props> = ({ serps }) => {
  const { formatMessage, messages, formatDate, locale } = useIntl();
  const { externalUrl } = useConfig();
  const { data } = useSearch();
  const { category, categoryValue, subcategory, subcategoryValue, url } = serps;
  const isSerpsMetadataToEdit = serps && [ANY, CITY, EMPLOYMENT_TYPE, ROLE].includes(category);
  const totalJobs = data?.jobSearchByQuery?.total ?? 0;
  const hasSubcategory = subcategory && subcategoryValue;

  const {
    metaTitle,
    metaDescription: description,
    paramName,
    subparamName,
  } = TRANSLATIONS_MAP[hasSubcategory ? `${category}-${subcategory}` : category];

  const values: Record<string, string> = {
    [paramName]: capitalizeFirstLetter(getCategoryTranslation(formatMessage, category, categoryValue)),
    ...(hasSubcategory &&
      subparamName && {
        [subparamName]: capitalizeFirstLetter(getCategoryTranslation(formatMessage, subcategory, subcategoryValue)),
      }),
    ...getSeoSerpsTitleTag(
      category,
      capitalizeFirstLetter(getCategoryTranslation(formatMessage, category, categoryValue)),
      subcategory,
      hasSubcategory ? getCategoryTranslation(formatMessage, subcategory, subcategoryValue) : subcategory
    ),
    postings: (totalJobs || 0).toString(),
    date: formatDate(new Date(), { year: 'numeric', month: 'short' }),
    month: formatDate(new Date(), { month: 'short' }),
    year: formatDate(new Date(), { year: 'numeric' }),
  };

  // We also have the need to check for specific description values for the given
  // category and subcategory. Such combinations have specific descriptions for the SEO
  const specificSubcategory = subcategoryValue ? `${subcategoryValue.toUpperCase()}_` : '';
  const specificValue = `${categoryValue.toUpperCase()}_${specificSubcategory}`;
  // Cast into string so Head's description does not complain
  const specificDescription = messages[`XJM_SEO_FOOTER_TEXT_${specificValue}DESCRIPTION`] as string | undefined;

  // to conditionally render a new description if the page is the local serps
  const metaDescription = isSerpsMetadataToEdit
    ? formatMessage(
        { id: description },
        getSeoSerpsMetaDescriptionValues(
          category,
          getCategoryTranslation(formatMessage, category, categoryValue),
          totalJobs,
          subcategory,
          hasSubcategory ? getCategoryTranslation(formatMessage, subcategory, subcategoryValue) : subcategory
        )
      )
    : specificDescription ?? formatMessage({ id: description }, values);

  // as this is just for german and just for two pages,
  // we change it just here, so we don't have to refactor everything just for a specific scenario
  let titleOverride = '';
  let descriptionOverride = '';

  const isEmploymentTypeCitySerp = metaTitle === 'XJM_META_TITLE_EMPLOYMENT_TYPE_CITY_WITH_DATE';
  const isEmploymentTypeAushilfeSerp = metaTitle === 'XJM_META_TITLE_JOB_TYPE_TERM';
  const isStudentOrFreelancerEmploymentType = ['Studierende', 'Selbstständig'].includes(values.employmentType);
  const isAushilfeEmploymentType = ['Aushilfe'].includes(values.employmentType);
  const isSkillSerp = metaTitle === 'XJM_META_TITLE_SKILL';

  if (isEmploymentTypeCitySerp && isStudentOrFreelancerEmploymentType && locale == 'de') {
    titleOverride = getTitleOverride(values);
    descriptionOverride = getDescriptionOverride(values);
  }

  if (isEmploymentTypeAushilfeSerp && isAushilfeEmploymentType && locale == 'de') {
    titleOverride = formatMessage({ id: 'XJM_META_TITLE_JOB_TYPE_TERM_MINIJOBS' }, values);
    descriptionOverride = formatMessage({ id: 'XJM_META_DESCRIPTION_JOB_TYPE_TERM_MINIJOBS' }, values);
  }

  if (isSkillSerp && locale === 'de') {
    titleOverride = `Aktuelle ${capitalizeFirstLetter(values.term)} Jobs - ${values.date}`;
  }

  return (
    <Head
      title={titleOverride ? titleOverride : formatMessage({ id: metaTitle }, values)}
      description={descriptionOverride ? descriptionOverride : metaDescription}
      canonicalUrl={`${externalUrl}${url}`}
      robots="noodp"
    />
  );
};
